import React from 'react';
import {
  Dialog,
  Button,
  FormControl,
  DialogTitle,
  DialogActions,
  IconButton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import FormInput from 'components/FormInput';
import moment from 'moment';
import { FormLabel } from 'react-bootstrap';
import { WorkFlowStatus } from 'components/common/WorkFlowCard/types';
import { IObservations } from 'redux/services/observation';
import { userRoles } from 'constants/constants';
import { WorkFlowDate } from 'components/common/WorkFlowDate';
import './StartObservationModal.scss';

type StartObservationModalProps = {
  open: boolean;
  observation: IObservations;
  onClose: () => void;
};

export const StartObservationModal: React.FC<StartObservationModalProps> = ({
  open,
  observation,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const project = useSelector((state: RootStates) =>
    state.project.projects.find((item) => item.projectInfo.id === projectId),
  );
  const subProjects = Object.values({
    subProject: observation.subProject?.name ?? '',
    subTask: observation.subTask?.name ?? '',
    endTask: observation.endTask?.name ?? '',
  })
    .filter((value) => value)
    .join(' -> ');

  const handleSubmit = () => {
    if (observation.status === WorkFlowStatus.DRAFT) {
      navigate(`/project/${projectId}/log-observation/${observation._id}/1`);
    } else {
      navigate(`/project/${projectId}/observation/${observation._id}/review`);
    }

    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getTitle = () => {
    if (observation.status === WorkFlowStatus.DRAFT) {
      return t('modal_title_complete_observation');
    }

    return t('modal_title_review_observation');
  };

  const getStartButtonLabel = () => {
    if (observation.status === WorkFlowStatus.DRAFT) {
      return t('btn_continue');
    }

    return t('btn_review');
  };

  const renderDate = () => {
    if (observation.status === WorkFlowStatus.DRAFT) {
      return (
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('label_draft_date')}>{`${t(
            'label_draft_date',
          )}: `}</FormLabel>
          <b className="px-3">
            {moment(observation.updated_at || observation.created_at).format(
              'DD/MM/YYYY',
            )}
          </b>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column">
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('label_observation_start_date')}>{`${t(
            'label_observation_start_date',
          )}: `}</FormLabel>
          <b className="px-3">
            {moment(
              observation.observationDate || observation.updated_at,
            ).format('DD/MM/YYYY')}
          </b>
        </div>
        {observation.status === WorkFlowStatus.CLOSE && (
          <div className="d-flex flex-nowrap justify-content-between">
            <FormLabel name={t('label_observation_close_date')}>{`${t(
              'label_observation_close_date',
            )}: `}</FormLabel>
            <b className="px-3">
              {moment(observation.updated_at).format('DD/MM/YYYY')}
            </b>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="start-inspection-modal"
      PaperProps={{
        style: {
          padding: 0,
          minWidth: 300,
        },
      }}
    >
      <DialogActions className="dialog-actions d-flex justify-content-between w-100 flex-no-wrap">
        <div className="d-flex flex-wrap align-items-center flex-grow-1">
          <DialogTitle className="p-1" color="inherit">
            {getTitle()}
          </DialogTitle>
          <div className="flex-grow-1 align-items-start">
            <span className={`status ${observation.status?.toLowerCase()}`}>
              {observation.status ?? WorkFlowStatus.OPEN}
            </span>
          </div>
        </div>
        <IconButton className="close-btn" onClick={onClose}>
          <Close color="inherit" />
        </IconButton>
      </DialogActions>
      <FormControl className="p-4 d-flex flex-column overflow-auto">
        <div className="d-flex flex-nowrap justify-content-between">
          <FormLabel name={t('observation_id')}>{`${t(
            'observation_id',
          )}:`}</FormLabel>
          <b className="px-3">{observation?.displayId}</b>
        </div>
        {renderDate()}
        {observation.observationInfo?.closeTill && (
          <WorkFlowDate
            date={observation.observationInfo.closeTill}
            status={observation.status ?? WorkFlowStatus.OPEN}
          />
        )}
        {(observation.observationInfo?.locations?.length ?? 0) > 0 && (
          <FormInput
            name={t('label_locations')}
            label={t('label_locations')}
            required={false}
            value={observation.observationInfo?.locations?.join(', ')}
            disabled
          />
        )}
        {subProjects.length > 0 && (
          <FormInput
            name={t('label_sub_projects')}
            label={t('label_sub_projects')}
            required={false}
            value={subProjects}
            disabled
          />
        )}
        <div className="d-flex flex-nowrap">
          <FormInput
            name={t('label_doer')}
            label={t('label_doer')}
            required={false}
            value={
              project?.team?.find(
                (teamObj) => teamObj.userId === observation.doer,
              )?.name ?? ''
            }
            disabled
          />
          <FormInput
            name={t('label_inspector')}
            label={t('label_inspector')}
            className="ml-1"
            required={false}
            value={
              project?.team?.find(
                (teamObj) => teamObj.userId === observation.inspector,
              )?.name ?? ''
            }
            disabled
          />
        </div>

        <footer className="p-0 mt-4 d-flex flex-row justify-content-between flex-grow-1 align-items-end">
          <Button variant="outlined" onClick={onClose}>
            {t('back')}
          </Button>
          <Button
            hidden={
              project?.role === userRoles.DOER &&
              observation.status === WorkFlowStatus.DRAFT
            }
            variant="contained"
            onClick={handleSubmit}
          >
            {getStartButtonLabel()}
          </Button>
        </footer>
      </FormControl>
    </Dialog>
  );
};
